import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, Menu } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MegaMenu } from "./MegaMenu";
import { MobileSubmenu } from "./MobileSubmenu";
import { ProfileMenu } from "./ProfileMenu";
import { SOBRE_DATA, SOLUCOES_DATA } from "./menu-data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<"sobre" | "solucoes" | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileView, setMobileView] = useState<"root" | "sobre" | "solucoes">(
    "root",
  );
  const { user, signOut } = useAuth();

  const closeTimerRef = useRef<number | null>(null);
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(
      () => setOpenMenu(null),
      120,
    ) as unknown as number;
  };
  useEffect(() => () => clearCloseTimer(), []);

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
        setProfileOpen(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed inset-x-0 top-0 z-50",
        "backdrop-blur-md transition-colors",
        scrolled ? "bg-n-green/95" : "bg-n-green",
      )}
    >
      <div className="flex h-[56px] container items-center justify-between md:h-[72px]">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/images/logo_xl.png"
            alt="Nacional GO"
            width={120}
            height={28}
            className="h-4 md:h-7 w-auto"
          />
          <span className="sr-only">Nacional GO</span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          <button
            className="group flex items-center text-white/90 hover:text-white"
            onMouseEnter={() => {
              clearCloseTimer();
              setOpenMenu("sobre");
            }}
            onFocus={() => {
              clearCloseTimer();
              setOpenMenu("sobre");
            }}
            onClick={() => setOpenMenu(openMenu === "sobre" ? null : "sobre")}
            onMouseLeave={scheduleClose}
            aria-expanded={openMenu === "sobre"}
          >
            Sobre{" "}
            <ChevronDown className="ml-1 h-4 w-4 transition group-aria-expanded:rotate-180" />
          </button>
          <button
            className="group flex items-center text-white/90 hover:text-white"
            onMouseEnter={() => {
              clearCloseTimer();
              setOpenMenu("solucoes");
            }}
            onFocus={() => {
              clearCloseTimer();
              setOpenMenu("solucoes");
            }}
            onClick={() =>
              setOpenMenu(openMenu === "solucoes" ? null : "solucoes")
            }
            onMouseLeave={scheduleClose}
            aria-expanded={openMenu === "solucoes"}
          >
            Soluções{" "}
            <ChevronDown className="ml-1 h-4 w-4 transition group-aria-expanded:rotate-180" />
          </button>
          <Link to="/blog" className="text-white/90 hover:text-white">
            Blog
          </Link>

          <MegaMenu
            data={SOBRE_DATA}
            open={openMenu === "sobre"}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            title="Sobre"
          />
          <MegaMenu
            data={SOLUCOES_DATA}
            open={openMenu === "solucoes"}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            title="Soluções"
          />
        </nav>

        <div className="flex items-center gap-3">
          {!user && (
            <div className="hidden items-center gap-3 lg:flex">
              <Link to="/login" className="text-white/90 hover:text-white">
                Entrar
              </Link>
            </div>
          )}
          <Link to="/fazer-cotacao" className="hidden lg:block">
            <Button variant="outline" size="small">
              Fazer Orçamento
            </Button>
          </Link>

          {user && (
            <ProfileMenu
              user={user}
              open={profileOpen}
              onToggle={() => setProfileOpen((v) => !v)}
              onSignOut={() => {
                signOut();
                setProfileOpen(false);
              }}
              onClose={() => setProfileOpen(false)}
            />
          )}

          <Sheet
            open={mobileOpen}
            onOpenChange={(o) => {
              setMobileOpen(o);
              if (!o) setMobileView("root");
            }}
          >
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-n-secondary lg:hidden hover:text-n-secondary/80"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full max-w-sm p-0">
              {mobileView === "root" ? (
                <div className="flex h-full flex-col">
                  <SheetHeader className="px-4 pb-2 pt-4">
                    <SheetTitle className="text-left text-n-text-green">
                      Nacional GO
                    </SheetTitle>
                  </SheetHeader>
                  <div className="space-y-1 p-2">
                    <button
                      className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-base hover:bg-n-green-h-light"
                      onClick={() => setMobileView("sobre")}
                    >
                      Sobre <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-base hover:bg-n-green-h-light"
                      onClick={() => setMobileView("solucoes")}
                    >
                      Soluções <ChevronRight className="h-4 w-4" />
                    </button>

                    {!user ? (
                      <Link
                        to="/login"
                        className="block rounded-md px-3 py-3 text-base hover:bg-n-green-h-light"
                        onClick={() => setMobileOpen(false)}
                      >
                        Entrar
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/cliente"
                          className="block rounded-md px-3 py-3 text-base hover:bg-n-green-h-light"
                          onClick={() => setMobileOpen(false)}
                        >
                          Área do Cliente
                        </Link>
                        <button
                          className="w-full rounded-md px-3 py-3 text-left text-base text-red-700 hover:bg-n-green-h-light"
                          onClick={() => {
                            signOut();
                            setMobileOpen(false);
                          }}
                        >
                          Sair
                        </button>
                      </>
                    )}

                    <div className="p-2">
                      <Link
                        to="/fazer-cotacao"
                        onClick={() => setMobileOpen(false)}
                      >
                        <Button variant="outline" size="small">
                          Fazer Orçamento
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <MobileSubmenu
                  title={mobileView === "sobre" ? "Sobre" : "Soluções"}
                  data={mobileView === "sobre" ? SOBRE_DATA : SOLUCOES_DATA}
                  onBack={() => setMobileView("root")}
                  onNavigate={() => setMobileOpen(false)}
                />
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
